/**
 * 使用量の画面（pages/money/UsagePage.tsx）・上のバーの残高（components/BalanceChip.tsx）・プロジェクトの概要の「今月の使用額」の
 * 「判断」だけを集めた純関数（2026-10-07 console/usage-view・founder「クリエイターは常にダッシュボードで自分のクレジット残高や、
 * DB やサーバー、転送料などの Usage を知りたいはず」）。
 *
 * WHY 分けるか: moneyLogic.ts と同じ（画面の部品を import しない = `node --test` で確かめられる。prepaidGate.ts も純関数だけなので例外的に読む）。文言はここに書かず、状態の名前を返す。
 * 数字そのもの（種類ごと・プロジェクトごとの額）はサーバーが台帳と同じ数字で返す（apps/server modules/money/usageViews.ts）。
 * ここは「どれを見せるか・どう言うか」だけ。
 */

import { notFundedYet } from './prepaidGate.ts';

// ---------------------------------------------------------------- 上のバーの残高

/**
 * 残高を「目立たせる」線（セント）。founder 2026-10-07「$5 を切ったら目立つ形（色・"Low"）」。
 * WHY サーバーの lowLineCents（知らせのメールの線）だけにしないか: 知らせの線は人が自由に下げられ（0 = オフ）、招待クレジットの間は
 * $1 まで下がる。上のバーは「いつも見える残高」なので、線は 1 つに決めて分かりやすくする。人が知らせの線を $5 より上に
 * 設定していれば、サーバーの balanceStatus 'low' も目立たせる（設定した線で目立つ = 驚かない）
 */
export const LOW_CHIP_CENTS = 500;

export type BalanceTone = 'ok' | 'low' | 'out' | 'pending' | 'start';

/**
 * 上のバーの残高の見た目。
 *   out … 止まっている（canSpend が false）か請求残がある = 赤
 *   low … $5 未満か、サーバーが low = 黄と "Low"
 *   pending … 招待クレジットがメールの確認待ち（確認すると入る）= 色なしで「メール確認待ち」
 *   WHY pending（2026-10-14 gtm/onboarding）: 以前は canSpend false = 赤の「Ran out / 残高なし」で、招待で登録した直後
 *     （確認前・$5 はまだ入っていない）の人が最初に見る画面の右上が赤い「残高なし」だった。招待制なので新しい人はほぼ全員これ。
 *     次に何をするか（メールの確認）を言う方が事実に近い。
 *   start … まだお金が入っていない（prepaidGate.ts notFundedYet = 帯の 'start' と同じ式）= 赤にせず「Add balance / 入金する」
 *   WHY start（2026-10-29 core/open-signup-polish）: 以前（10/14）は招待なしの 0 を「使い切った人と見分けられない」として赤の
 *     「RAN OUT」のままにした。10/31 から招待なしで登録できる（founder 10/29）= 新しい人は全員 $0 で、最初の画面の右上が
 *     「RAN OUT」になる（一度も入れていないのに「尽きた」）。帯（prepaidBand）はすでに 'start' / 'empty' を分けているので同じ式に揃えた。
 *     ちょうど 0 まで使い切った人も start に見える限界は notFundedYet のコメントのとおり（次にすることは同じ「入金」）
 * autoTopUp … 自動チャージが ON（「少なくなったら自動で入る」を一緒に見せる = 黄でも慌てなくてよいと分かる）
 */
export function balanceChip(p: {
  balance: number;
  owed: number;
  canSpend?: boolean;
  balanceStatus?: string;
  autoTopUp?: { enabled: boolean } | null;
  /** GET /v1/money/prepaid の invite（inviteCode.ts InviteStanding）。古い本体・招待なしは null / 省略 */
  invite?: { granted: number; pending?: boolean } | null;
}): { tone: BalanceTone; autoTopUp: boolean } {
  const autoTopUp = p.autoTopUp?.enabled === true;
  if (p.owed > 0) return { tone: 'out', autoTopUp };
  if (p.canSpend === false) {
    // prepaidGate.ts prepaidBand の 'invitePending' と同じ見分け（ホームの段と上のバーで食い違わない）
    if (p.invite?.pending === true) return { tone: 'pending', autoTopUp };
    // 招待を使い切った人（granted > 0）・残高がマイナスの人は 'out'（本当に「使い切った」）
    if (notFundedYet(p)) return { tone: 'start', autoTopUp };
    return { tone: 'out', autoTopUp };
  }
  if (p.balance < LOW_CHIP_CENTS || p.balanceStatus === 'low' || p.balanceStatus === 'out') return { tone: 'low', autoTopUp };
  return { tone: 'ok', autoTopUp };
}

// ---------------------------------------------------------------- 種類ごとの「どれだけ使ったか」

/** サーバーの種類（apps/server usageViews.ts USAGE_CATEGORIES と同じ名前）。知らない種類が来ても落とさない（string） */
export type UsageCategoryName = 'database' | 'platform' | 'functions' | 'transfer' | 'storage' | 'multiplayer' | 'domains' | 'email' | 'ai' | 'x_sign_in' | 'other' | (string & {});

export type AmountKind = 'requests' | 'gbTransferred' | 'gbStored' | 'roomHours' | 'playerHours' | 'emails' | 'domains' | 'signIns';

const GB = 1_000_000_000; // 10 億バイト（Cloudflare の請求の GB と同じ十進の GB）
const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * 種類ごとに 1 つだけ、利用者が分かる量を選ぶ（中の項目名・GB 秒・バイト日などを見せない = docs/brand §5）。
 * daysSoFar = その月の経った日数（締めた月はその月の日数）。保存量・独自ドメインは「日ごとの最大の合計」なので、日数で割って平均にする。
 * 量が無い・言いようが無い（AI = 金額でしか測らない）なら null（画面は金額だけを出す）
 */
export function categoryAmount(category: UsageCategoryName, quantities: Array<{ meter: string; quantity: number }>, daysSoFar: number): { kind: AmountKind; value: number } | null {
  const q = (...meters: string[]) => quantities.filter((x) => meters.includes(x.meter)).reduce((a, x) => a + x.quantity, 0);
  const has = (...meters: string[]) => quantities.some((x) => meters.includes(x.meter) && x.quantity > 0);
  const days = Math.max(1, daysSoFar);
  switch (category) {
    case 'database':
    // platform = どの機能にも当たらない Kumo の API の呼び出し（2026-10-15 creator/academy-friction でサーバーが database から分けた）
    case 'platform':
      return has('api_requests') ? { kind: 'requests', value: q('api_requests') } : null;
    case 'functions':
      return has('compute_requests') ? { kind: 'requests', value: q('compute_requests') } : null;
    case 'transfer':
      return has('hosting_egress_bytes') ? { kind: 'gbTransferred', value: round2(q('hosting_egress_bytes') / GB) } : null;
    case 'storage':
      return has('hosting_storage_byte_days') ? { kind: 'gbStored', value: round2(q('hosting_storage_byte_days') / days / GB) } : null;
    case 'multiplayer':
      // ルームが開いていた時間が一番分かりやすい。ルームを使わない形（音声だけ等）は人のつながっていた時間
      if (has('realtime_room_seconds')) return { kind: 'roomHours', value: round2(q('realtime_room_seconds') / 3600) };
      return has('realtime_connection_seconds') ? { kind: 'playerHours', value: round2(q('realtime_connection_seconds') / 3600) } : null;
    case 'email':
      return has('mail_sends', 'mail_broadcast_sends') ? { kind: 'emails', value: q('mail_sends', 'mail_broadcast_sends') } : null;
    case 'domains':
      return has('hosting_custom_hostname_days', 'functions_custom_hostname_days')
        ? { kind: 'domains', value: Math.max(1, Math.round(q('hosting_custom_hostname_days', 'functions_custom_hostname_days') / days)) }
        : null;
    case 'x_sign_in':
      return has('x_api_user_reads') ? { kind: 'signIns', value: q('x_api_user_reads') } : null;
    default:
      return null;
  }
}

export interface UsageRow {
  key: string;
  /** category = サーバーの種類（新しい本体）/ component = 部品（古い本体 = byCategory が無い） */
  source: 'category' | 'component';
  costMicros: number;
  quantities: Array<{ meter: string; quantity: number }>;
}

/** 内訳の行（額の大きい順）。古い本体（byCategory が無い）は今までの部品の行で出す = 画面が空にならない */
export function usageRows(u: {
  byCategory?: Array<{ category: string; costMicros: number; quantities: Array<{ meter: string; quantity: number }> }>;
  byComponent: Array<{ component: string; costMicros: number }>;
}): UsageRow[] {
  const rows: UsageRow[] = Array.isArray(u.byCategory)
    ? u.byCategory.map((c) => ({ key: c.category, source: 'category', costMicros: c.costMicros, quantities: c.quantities ?? [] }))
    : u.byComponent.map((c) => ({ key: c.component, source: 'component', costMicros: c.costMicros, quantities: [] }));
  return rows.sort((a, b) => b.costMicros - a.costMicros);
}

/** 今日（UTC = サーバーが日を区切る時計）の額（日ごとの目安から）。まだ無ければ 0 */
export function todayMicros(daily: { days: Array<{ day: string; costMicros: number }> }, now: Date): number {
  const today = now.toISOString().slice(0, 10);
  return daily.days.find((d) => d.day === today)?.costMicros ?? 0;
}

/** その月の経った日数（UTC）。過ぎた月はその月の日数・先の月は 0 */
export function daysSoFar(period: string, now: Date): number {
  const [y, m] = period.split('-').map(Number) as [number, number];
  const start = Date.UTC(y, m - 1, 1);
  const end = Date.UTC(y, m, 1);
  const t = now.getTime();
  if (t < start) return 0;
  if (t >= end) return Math.round((end - start) / 86_400_000);
  return Math.floor((t - start) / 86_400_000) + 1;
}

// ---------------------------------------------------------------- 残りの目安

/**
 * 「あと何日もつか」の言い方（サーバーの daysLeft = 今月のペースでの目安）。
 *   out     … 止まっている（請求残がある）
 *   noUsage … 今月まだ使っていない（daysLeft が null）= 減っていない
 *   early   … 月の初め（使った日が EARLY_DAYS 日以下）= ペースの元が少ない。日数と一緒に「まだ {n} 日分の利用から」と言う
 *   days    … ふつう
 *   unknown … 古い本体（daysLeft が無い）
 * WHY early を分けるか: サーバーの目安は今月の利用の平均。1〜3 日目は 1 回の試しの多い日で大きく振れる（founder「データが少ないときも分かる言い方で」）
 */
export const EARLY_DAYS = 3;
export type RunwayView = { kind: 'out' } | { kind: 'noUsage' } | { kind: 'unknown' } | { kind: 'days'; days: number } | { kind: 'early'; days: number; daysOfData: number };

export function runwayView(p: { owed: number; balance: number; daysLeft?: number | null }, now: Date): RunwayView {
  if (p.owed > 0) return { kind: 'out' };
  if (p.daysLeft === undefined) return { kind: 'unknown' };
  if (p.daysLeft === null) return { kind: 'noUsage' };
  const day = now.getUTCDate();
  if (day <= EARLY_DAYS) return { kind: 'early', days: p.daysLeft, daysOfData: day };
  return { kind: 'days', days: p.daysLeft };
}
